import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Slots.css";
import ClipLoader from "react-spinners/ClipLoader";
import LoadingPage from "../../pages/LoadingPage";
import Modal from "react-modal";
import { FaCheck, FaLock, FaMinus } from "react-icons/fa";

const overrideSmall = {
  display: "flex",
  borderWidth: "3.5px",
  height: "30px",
  width: "30px",
  position: "absolute",
  left: "calc(50% - 15px)",
  top: "calc(50% - 15px)",
  bottom: "45px",
};

const SlotsTable = (props) => {
  const [slotWindows, setSlotWindows] = useState([]); // Renamed to slotWindows for consistency
  const date = new Date();
  const [isFirst, setIsFirst] = useState(true);
  const [startDate, setStartDate] = useState(date);
  const endDate = new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000);
  const [currentStartDate, setCurrentStartDate] = useState(startDate);
  const [slotsData, setSlotsData] = useState({});
  const [numColumns, setNumColumns] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [highestDaysNoticeItems, setHighestDaysNoticeItems] = useState({});
  const [modalIsOpen, setIsOpen] = useState(false);
  const [highlightedDates, setHighlightedDates] = useState([]);

  useEffect(() => {
    setIsFirst(false);
    //May need to add && to check when basket is empty as it may return a {}
    if (props.basket.lines) {
      let maxDaysNoticeItems = {};
      let highestDays = 0;
      const date = new Date();

      for (const { node: item } of props.basket.lines.edges) {
        const days = parseInt(5); // Placeholder for the actual logic to calculate days!!!!!!!!

        if (days > highestDays) {
          highestDays = days;
          maxDaysNoticeItems = {};
          maxDaysNoticeItems[item.merchandise.product.title] = days;
        } else if (days === highestDays) {
          maxDaysNoticeItems[item.merchandise.product.title] = days;
        }
      }
      setStartDate(
        new Date(date.getTime() + highestDays * 24 * 60 * 60 * 1000)
      );

      setHighestDaysNoticeItems(maxDaysNoticeItems);
    } else {
      setStartDate(new Date());
    }
  }, [props.basket]);

  useEffect(() => {
    setCurrentStartDate(startDate);
  }, [startDate]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const goToNextPeriod = () => {
    const nextStartDate = new Date(
      currentStartDate.getTime() + numColumns * 24 * 60 * 60 * 1000
    );
    const nextEndDate = new Date(
      nextStartDate.getTime() + (numColumns - 1) * 24 * 60 * 60 * 1000
    );

    if (nextEndDate <= endDate) {
      setCurrentStartDate(nextStartDate);
    } else {
      setCurrentStartDate(
        new Date(endDate.getTime() - (numColumns - 1) * 24 * 60 * 60 * 1000)
      );
    }
  };

  const goToPreviousPeriod = () => {
    const previousStartDate = new Date(
      currentStartDate.getTime() - numColumns * 24 * 60 * 60 * 1000
    );
    const previousEndDate = new Date(
      previousStartDate.getTime() + (numColumns - 1) * 24 * 60 * 60 * 1000
    );

    if (previousStartDate >= startDate && previousEndDate <= endDate) {
      setCurrentStartDate(previousStartDate);
    } else {
      setCurrentStartDate(startDate);
    }
  };

  const handleDateChange = (date) => {
    const newStartDate = new Date(date.getTime());
    const newEndDate = new Date(
      newStartDate.getTime() + (numColumns - 1) * 24 * 60 * 60 * 1000
    );

    if (newStartDate >= startDate) {
      if (newEndDate <= endDate) {
        setCurrentStartDate(newStartDate);
      } else {
        setCurrentStartDate(
          new Date(endDate.getTime() - (numColumns - 1) * 24 * 60 * 60 * 1000)
        );
      }
    }
  };

  const formatColumnHeader = (date) => {
    const options = { weekday: "short", day: "numeric", month: "short" };
    return date.toLocaleDateString("en-GB", options).replace(",", "");
  };

  const generateDates = () => {
    const dates = [];
    for (let i = 0; i < numColumns; i++) {
      const currentDate = new Date(
        currentStartDate.getTime() + i * 24 * 60 * 60 * 1000
      );
      dates.push(currentDate);
    }
    return dates;
  };

  const handleSlotClick = (date, slotWindow) => {
    const slotKey = `${date.toISOString().slice(0, 10)} ${slotWindow}`;
    const updatedSlotsData = { ...slotsData };

    // If the clicked slot is already selected, unselect it
    if (props.slot === slotKey) {
      updatedSlotsData[slotKey].isSelected = false;
      props.setSlot(null);
    } else if (updatedSlotsData[slotKey].status === "available") {
      // Mark slot as selected
      updatedSlotsData[slotKey].isSelected = true;

      // Unselect previously selected slot
      if (props.slot && updatedSlotsData[props.slot]) {
        updatedSlotsData[props.slot].isSelected = false;
      }

      props.setSlot(slotKey);
    }

    setSlotsData(updatedSlotsData);
  };

  const getSlotButtonContent = (date, slotWindow) => {
    const slotKey = `${date.toISOString().slice(0, 10)} ${slotWindow}`;
    const slotData = slotsData[slotKey];
    const isSelected = props.slot === slotKey;

    if (isSelected) {
      return <FaCheck style={{ marginTop: "2px" }} />;
    }

    if (!slotData || slotData.status === "unavailable") {
      return <FaMinus style={{ marginTop: "3px" }} />;
    }

    return (
      <div style={{ lineHeight: "20px" }}>
        <b>{slotData.totalAvailable}</b> <br />{" "}
        {slotData.status.charAt(0).toUpperCase() + slotData.status.slice(1)}
      </div>
    );
  };

  const updateNumColumns = useCallback(() => {
    const width = window.innerWidth;
    let columns = 3;

    if (width >= 800) columns = 5;
    else if (width >= 500) columns = 4;

    if (columns !== numColumns) {
      setNumColumns(columns);
    }
  }, [numColumns]);

  useEffect(() => {
    window.addEventListener("resize", updateNumColumns);

    return () => {
      window.removeEventListener("resize", updateNumColumns);
    };
  }, [updateNumColumns]);

  useEffect(() => {
    updateNumColumns();
  }, [updateNumColumns]);

  useEffect(() => {
    // If first render then don't run as dates wont be set correctly
    if (isFirst) {
      return;
    }
    const fetchDates = async () => {
      const startDateString = startDate.toISOString().slice(0, 10);
      const endDateString = endDate.toISOString().slice(0, 10);

      try {
        setIsLoading(true); // Start loading

        // Simulating an artificial delay
        await delay(500);

        const availableSlots = await props.getDates(
          startDateString,
          endDateString
        );
        const updatedSlotsData = { ...slotsData };

        //Highlight all available dates on calendar date picker
        highlightAvailableDates(availableSlots, startDateString);

        // Extract and sort slot windows
        const extractedSlotWindows = new Set();
        Object.keys(availableSlots).forEach((slotKey) => {
          const slotWindow = slotKey.split(" ")[1]; // e.g. "09:00-10:00"
          extractedSlotWindows.add(slotWindow);
        });

        // Convert Set to Array and sort by start time
        const sortedSlotWindows = [...extractedSlotWindows].sort((a, b) => {
          const getStartHour = (window) => parseInt(window.split(":")[0], 10);
          return getStartHour(a) - getStartHour(b);
        });

        setSlotWindows(sortedSlotWindows); // Set the sorted windows

        Object.entries(availableSlots).forEach(([slotDate, slotCount]) => {
          updatedSlotsData[slotDate] = {
            totalAvailable: slotCount,
            status: slotCount > 0 ? "available" : "unavailable",
            isSelected: false,
          };
        });

        setSlotsData(updatedSlotsData);
      } catch (error) {
        console.error("Error fetching or processing dates:", error);
      } finally {
        setIsLoading(false); // Stop loading
      }
    };

    fetchDates();
    // eslint-disable-next-line
  }, [currentStartDate, numColumns, props.slotTaken]);

  const highlightAvailableDates = (availableSlots, startDateString) => {
    const dates = [];
    const start = new Date(startDateString);

    for (const slotKey in availableSlots) {
      const [dateStr] = slotKey.split(" ");
      const slotDate = new Date(dateStr);

      if (availableSlots[slotKey] > 0 && slotDate >= start) {
        dates.push(slotDate);
      }
    }
    dates.length === 0 ? props.setNoAvailableSlots(true) : props.setNoAvailableSlots(false);
    setHighlightedDates(dates);
  };

  const isLastPeriod = () => {
    const nextStartDate = new Date(
      currentStartDate.getTime() + numColumns * 24 * 60 * 60 * 1000
    );
    const nextEndDate = new Date(nextStartDate.getTime());

    return nextEndDate > endDate;
  };

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  Modal.setAppElement("#root");
  return (
      <div
          className={
            props.invalidFields.includes("Pick a slot")
                ? "slot_container invalid"
                : "slot_container"
          }
      >
        {/* STEP 1: Wait for basket and loading */}
        {!props.basket.id ? (
            <LoadingPage height="110px"/>
        ) : props.reservedSlot ? (
            // STEP 2: Show reserved message if it exists
            <div className="reserved_slot_message_container">
              <FaLock className="reserved_slot_icon"/>
              <span className="reserved_slot_message">
        You currently have a slot reserved at{" "}
                <span className="highlighted_time">
          {props.formatReservedSlotDate(props.reservedSlot.date)}
        </span>{" "}
                which will expire at{" "}
                <span className="highlighted_time">
          {props.formatReservationExpiryTime(props.reservedSlot.expiresAt)}
        </span>
        . Proceed to payment and complete your order to avoid losing your slot.
      </span>
            </div>
        ) : (
            // STEP 3: Show table navigation and slot grid
            <>
              <div className="slot_nav_container">
                <button
                    type="button"
                    className={
                      currentStartDate.toISOString().slice(0, 10) ===
                      startDate.toISOString().slice(0, 10) ||
                      highlightedDates.length === 0
                          ? "slot_nav_prev inactive"
                          : "slot_nav_prev"
                    }
                    onClick={goToPreviousPeriod}
                >
                  ⇦ PREV
                </button>

                <div
                    className={
                      highlightedDates.length === 0
                          ? "date_picker_container inactive"
                          : "date_picker_container"
                    }
                >
                  <DatePicker
                      className="date_picker"
                      selected={currentStartDate}
                      onChange={handleDateChange}
                      minDate={startDate}
                      maxDate={endDate}
                      dateFormat="yyyy-mm-dd"
                      placeholderText="View calendar"
                      highlightDates={highlightedDates}
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                  />
                  <div className="calendar_icon_txt_container">
                    <img
                        className="calendar_icon"
                        alt="Calendar Icon"
                        src={require("./../../../images/calendar_icon.png")}
                        loading="eager"
                    />
                    <h4 className="date_picker_txt">CALENDAR</h4>
                  </div>
                </div>

                <button
                    type="button"
                    className={
                      isLastPeriod() || highlightedDates.length === 0
                          ? "slot_nav_next inactive"
                          : "slot_nav_next"
                    }
                    onClick={goToNextPeriod}
                >
                  NEXT ⇨
                </button>
              </div>

              {/* STEP 4: Handle no available dates */}
              {highlightedDates.length === 0 && !isLoading ? (
                  <div className="slots_table_info_container">
                    <img
                        className="slots_table_info_icon"
                        alt="No Slots"
                        src={require("./../../../images/icons8-empty.png")}
                        loading="eager"
                    />
                    <span className="slots_table_info_txt">
            There are currently no slot dates available, sorry.
          </span>
                  </div>
              ) : (
                  <table className="slot_table">
                    <thead>
                    <tr>
                      <th className="slot_row_header"></th>
                      {generateDates().map((date) => (
                          <th className="slot_col_header" key={date}>
                            {formatColumnHeader(date)}
                          </th>
                      ))}
                    </tr>
                    </thead>
                    <tbody>
                    {isLoading || props.reservedSlotLoading ? (
                        <tr>
                          <td className="t_loading_container" colSpan={numColumns + 1}>
                            <ClipLoader
                                color="#001e00"
                                loading={true}
                                cssOverride={overrideSmall}
                                size="50px"
                                aria-label="Loading Spinner"
                                data-testid="loader"
                            />
                          </td>
                        </tr>
                    ) : (
                        slotWindows.map((slotWindow) => (
                            <tr key={slotWindow}>
                              <th className="slot_row_header">{slotWindow}</th>
                              {generateDates().map((date) => {
                                const slotKey = `${date.toISOString().slice(0, 10)} ${slotWindow}`;
                                const slotData = slotsData[slotKey];
                                const isSelected = props.slot === slotKey;

                                const buttonClassName = () => {
                                  if (isSelected) return "slot selected";
                                  if (slotData?.status === "available") return "slot available";
                                  return "slot unavailable";
                                };

                                return (
                                    <td key={date}>
                                      <button
                                          type="button"
                                          className={buttonClassName()}
                                          onClick={() => handleSlotClick(date, slotWindow)}
                                          disabled={isLoading}
                                      >
                                        {getSlotButtonContent(date, slotWindow)}
                                      </button>
                                    </td>
                                );
                              })}
                            </tr>
                        ))
                    )}
                    </tbody>
                  </table>
              )}

              {/* STEP 5: Days notice dialog/modal */}
              {Object.keys(highestDaysNoticeItems).length !== 0 &&
                  highlightedDates.length !== 0 && (
                      <>
                        <p className="days_notice_dialog" onClick={openModal}>
                          Date selection from <b>{formatColumnHeader(startDate)}</b> due to
                          notice required for{" "}
                          <span className="item_count">
                {Object.keys(highestDaysNoticeItems).length}{" "}
                            {Object.keys(highestDaysNoticeItems).length === 1
                                ? "item"
                                : "items"}
              </span>{" "}
                          in your basket
                        </p>
                        <Modal
                            isOpen={modalIsOpen}
                            onRequestClose={closeModal}
                            contentLabel="Item Notice"
                            className="InfoModal"
                            overlayClassName="Overlay"
                        >
                          <div className="modal_exit" onClick={closeModal}></div>
                          <span className="modal_days_notice_title">
                <b>{Object.values(highestDaysNoticeItems)[0]} days </b>notice for:
              </span>
                          {Object.keys(highestDaysNoticeItems).map((key, i, arr) => (
                              <React.Fragment key={key}>
                  <span className="modal_days_notice_item">
                    {key}
                    {i !== arr.length - 1 && ","}
                  </span>
                              </React.Fragment>
                          ))}
                        </Modal>
                      </>
                  )}
            </>
        )}
      </div>
  );
};

export default SlotsTable;
